// Scoring Transparency Utility
// Provides detailed breakdown of how scores were calculated

/**
 * Generate detailed score breakdown for transparency
 * @param {Object} formData - Raw form data from all sections
 * @param {Object} scores - Calculated dimension scores
 * @returns {Object} - Detailed breakdown per dimension
 */
export const generateScoreBreakdown = (formData, scores) => {
  const strategy = formData.strategy_dimension || {};
  const data = formData.data_dimension || {};
  const technology = formData.technology_dimension || {};
  const people = formData.people_dimension || {};
  const governance = formData.governance_dimension || {};
  const currentState = formData.current_state || {};

  return {
    strategy: {
      score: scores.strategy,
      fields: [
        strategy.ai_strategy_exists !== undefined && {
          label: 'AI Strategy Documentation',
          value: getRadioOptionLabel('ai_strategy_exists', strategy.ai_strategy_exists),
          score: calculateFieldScore('ai_strategy_exists', strategy.ai_strategy_exists, 4)
        },
        strategy.business_alignment !== undefined && {
          label: 'Business Alignment',
          value: `${strategy.business_alignment}/5`,
          score: parseInt(strategy.business_alignment) || 1
        },
        strategy.success_metrics !== undefined && {
          label: 'Success Metrics Defined',
          value: getRadioOptionLabel('success_metrics', strategy.success_metrics),
          score: calculateFieldScore('success_metrics', strategy.success_metrics, 4)
        },
        strategy.executive_sponsorship !== undefined && {
          label: 'Executive Sponsorship',
          value: getRadioOptionLabel('executive_sponsorship', strategy.executive_sponsorship),
          score: calculateFieldScore('executive_sponsorship', strategy.executive_sponsorship, 4)
        },
      ].filter(Boolean)
    },
    
    data: {
      score: scores.data,
      fields: [
        data.data_inventory !== undefined && {
          label: 'Data Inventory',
          value: getRadioOptionLabel('data_inventory', data.data_inventory),
          score: calculateFieldScore('data_inventory', data.data_inventory, 5)
        },
        data.data_quality !== undefined && {
          label: 'Data Quality',
          value: `${data.data_quality}/5`,
          score: parseInt(data.data_quality) || 1
        },
        data.data_integration !== undefined && {
          label: 'Data Integration',
          value: getRadioOptionLabel('data_integration', data.data_integration),
          score: calculateFieldScore('data_integration', data.data_integration, 4)
        },
        data.data_security && Array.isArray(data.data_security) && {
          label: 'Security Controls',
          value: data.data_security.includes('None of the above') ? 
            'None' : `${data.data_security.length} controls implemented`,
          score: data.data_security.includes('None of the above') ? 1 :
            Math.min(5, (data.data_security.length / 7) * 5)
        },
        data.structured_vs_unstructured && Array.isArray(data.structured_vs_unstructured) && {
          label: 'Data Type Diversity',
          value: `${data.structured_vs_unstructured.length} types`,
          score: Math.min(5, (data.structured_vs_unstructured.length / 8) * 4 + 1)
        },
        data.data_cleansing_needed !== undefined && {
          label: 'Data Readiness',
          value: getRadioOptionLabel('data_cleansing_needed', data.data_cleansing_needed),
          score: [1, 2, 3, 5, 3][parseInt(data.data_cleansing_needed) || 0]
        },
      ].filter(Boolean)
    },
    
    technology: {
      score: scores.technology,
      fields: [
        technology.cloud_readiness !== undefined && {
          label: 'Cloud Deployment Preference',
          value: getRadioOptionLabel('cloud_readiness', technology.cloud_readiness),
          score: calculateFieldScore('cloud_readiness', technology.cloud_readiness, 4)
        },
        technology.cloud_providers && Array.isArray(technology.cloud_providers) && {
          label: 'Cloud Provider Diversity',
          value: technology.cloud_providers.includes('On-premises only') ? 'On-premises only' :
            technology.cloud_providers.includes('Not yet determined') ? 'Not determined' :
            `${technology.cloud_providers.length} providers`,
          score: technology.cloud_providers.includes('On-premises only') || 
                 technology.cloud_providers.includes('Not yet determined') ? 2 :
            Math.min(5, technology.cloud_providers.length + 2)
        },
        technology.erp_systems && Array.isArray(technology.erp_systems) && {
          label: 'Enterprise Systems',
          value: `${technology.erp_systems.length} systems`,
          score: Math.min(5, (technology.erp_systems.length / 5) * 3 + 1)
        },
        technology.api_maturity !== undefined && {
          label: 'AI Integration Level',
          value: getRadioOptionLabel('api_maturity', technology.api_maturity),
          score: calculateFieldScore('api_maturity', technology.api_maturity, 4)
        },
        technology.dev_resources !== undefined && {
          label: 'Development Resources',
          value: getRadioOptionLabel('dev_resources', technology.dev_resources),
          score: calculateFieldScore('dev_resources', technology.dev_resources, 5)
        },
        technology.infrastructure_constraints && Array.isArray(technology.infrastructure_constraints) && {
          label: 'Infrastructure Constraints',
          value: technology.infrastructure_constraints.includes('No major constraints') ?
            'No major constraints' : `${technology.infrastructure_constraints.length} constraints`,
          score: technology.infrastructure_constraints.includes('No major constraints') ? 5 :
            Math.max(1, 5 - (technology.infrastructure_constraints.length * 0.6))
        },
        currentState.ai_tools_in_use && Array.isArray(currentState.ai_tools_in_use) && {
          label: 'AI Tools in Use',
          value: currentState.ai_tools_in_use.includes('None') ? 'None' :
            `${currentState.ai_tools_in_use.length} tools`,
          score: currentState.ai_tools_in_use.includes('None') ? 1 :
            Math.min(5, (currentState.ai_tools_in_use.length / 6) * 4 + 1)
        },
      ].filter(Boolean)
    },
    
    people: {
      score: scores.people,
      fields: [
        people.ai_literacy !== undefined && {
          label: 'AI Literacy Level',
          value: getRadioOptionLabel('ai_literacy', people.ai_literacy),
          score: calculateFieldScore('ai_literacy', people.ai_literacy, 5)
        },
        people.training_provided !== undefined && {
          label: 'Training Programs',
          value: getRadioOptionLabel('training_provided', people.training_provided),
          score: calculateFieldScore('training_provided', people.training_provided, 4)
        },
        people.change_readiness !== undefined && {
          label: 'Change Readiness',
          value: `${people.change_readiness}/5`,
          score: parseInt(people.change_readiness) || 1
        },
        people.dedicated_roles && Array.isArray(people.dedicated_roles) && {
          label: 'Dedicated AI Roles',
          value: people.dedicated_roles.includes('No dedicated AI roles') ? 'None' :
            `${people.dedicated_roles.length} roles`,
          score: people.dedicated_roles.includes('No dedicated AI roles') ? 1 :
            Math.min(5, (people.dedicated_roles.length / 5) * 4 + 1)
        },
        currentState.ai_usage_scope && {
          label: 'AI Usage Scope',
          value: currentState.ai_usage_scope,
          score: {
            'Less than 5%': 1,
            '5-25%': 2,
            '25-50%': 3,
            '50-75%': 4,
            'More than 75%': 5,
            'Unknown': 2.5
          }[currentState.ai_usage_scope] || 2.5
        },
        people.staff_burden_concerns && Array.isArray(people.staff_burden_concerns) && {
          label: 'Staff Concerns',
          value: people.staff_burden_concerns.includes('No major concerns') ? 'No major concerns' :
            `${people.staff_burden_concerns.length} concerns`,
          score: people.staff_burden_concerns.includes('No major concerns') ? 5 :
            Math.max(1, 5 - (people.staff_burden_concerns.length * 0.5))
        },
      ].filter(Boolean)
    },
    
    governance: {
      score: scores.governance,
      fields: [
        governance.governance_framework !== undefined && {
          label: 'Change Management Approach',
          value: getRadioOptionLabel('governance_framework', governance.governance_framework),
          score: calculateFieldScore('governance_framework', governance.governance_framework, 4)
        },
        governance.ethical_guidelines !== undefined && {
          label: 'Ethical AI Principles',
          value: getRadioOptionLabel('ethical_guidelines', governance.ethical_guidelines),
          score: calculateFieldScore('ethical_guidelines', governance.ethical_guidelines, 5)
        },
        governance.compliance_requirements && Array.isArray(governance.compliance_requirements) && {
          label: 'Compliance Requirements',
          value: governance.compliance_requirements.includes('None') ? 'None' :
            `${governance.compliance_requirements.length} frameworks`,
          score: governance.compliance_requirements.includes('None') ? 2 :
            Math.min(5, (governance.compliance_requirements.length / 5) * 3 + 2)
        },
        governance.risk_assessment !== undefined && {
          label: 'Risk Assessment',
          value: getRadioOptionLabel('risk_assessment', governance.risk_assessment),
          score: calculateFieldScore('risk_assessment', governance.risk_assessment, 5)
        },
        governance.accountability !== undefined && {
          label: 'Accountability Framework',
          value: getRadioOptionLabel('accountability', governance.accountability),
          score: calculateFieldScore('accountability', governance.accountability, 5)
        },
        governance.data_governance && Array.isArray(governance.data_governance) && {
          label: 'Data Governance Practices',
          value: governance.data_governance.includes('Limited or no data governance') ? 'Limited' :
            `${governance.data_governance.length} practices`,
          score: governance.data_governance.includes('Limited or no data governance') ? 1 :
            Math.min(5, (governance.data_governance.length / 6) * 4 + 1)
        },
      ].filter(Boolean)
    }
  };
};

// Helper to calculate field score (mimics scoring logic)
const calculateFieldScore = (fieldId, value, maxOptions) => {
  const index = typeof value === 'string' ? parseInt(value) : value;
  if (isNaN(index)) return 1;
  return Math.min(5, Math.max(1, Math.ceil(((index + 1) / maxOptions) * 5)));
};

// Helper to get human-readable label for radio options
const getRadioOptionLabel = (fieldId, value) => {
  const labels = {
    ai_strategy_exists: [
      'No formal strategy',
      'Informal initiatives',
      'Documented strategy',
      'Integrated with business'
    ],
    success_metrics: [
      'No metrics',
      'Basic usage metrics',
      'ROI metrics',
      'Strategic KPIs'
    ],
    executive_sponsorship: [
      'No awareness',
      'Awareness exists',
      'Active sponsorship',
      'Board-level oversight'
    ],
    data_inventory: [
      'No inventory',
      'Partial inventory',
      'Key systems cataloged',
      'Comprehensive inventory',
      'Full catalog with lineage'
    ],
    data_integration: [
      'Siloed',
      'Limited access',
      'Centralized platforms',
      'Real-time access'
    ],
    data_cleansing_needed: [
      'Significant cleansing',
      'Moderate cleansing',
      'Minor cleanup',
      'Data ready',
      'Unsure'
    ],
    cloud_readiness: [
      'Cloud-based',
      'On-premise',
      'Hybrid',
      'Not sure'
    ],
    api_maturity: [
      'No integration',
      'Standalone tools',
      'Integrated workflows',
      'Fully embedded'
    ],
    dev_resources: [
      'No IT team',
      'Small support team',
      'Maintenance team',
      'Full dev capability',
      'Strong engineering with AI'
    ],
    ai_literacy: [
      'Very low',
      'Low',
      'Moderate',
      'Good',
      'High'
    ],
    training_provided: [
      'No training',
      'Ad-hoc learning',
      'Structured programs',
      'Continuous learning culture'
    ],
    governance_framework: [
      'No change management',
      'Basic communication',
      'Structured process',
      'Proactive innovation culture'
    ],
    ethical_guidelines: [
      'No guidelines',
      'Under discussion',
      'Documented principles',
      'Principles with enforcement',
      'Comprehensive ethics program'
    ],
    risk_assessment: [
      'No assessment',
      'Initial identification',
      'Formal assessment',
      'Ongoing monitoring',
      'Comprehensive program'
    ],
    accountability: [
      'Not defined',
      'Informally assigned',
      'Documented ownership',
      'Clear RACI',
      'Comprehensive framework'
    ]
  };

  const index = typeof value === 'string' ? parseInt(value) : value;
  return labels[fieldId]?.[index] || `Option ${index + 1}`;
};

